import ls from 'local-storage';
import qs from 'querystring';
import * as Sentry from '@sentry/browser';
import { mapRoot } from '@/mixins/root';

import AuthResumeSessionQuery from '@/graphql/queries/AuthResumeSession.gql';
import CurrentCompanyQuery from '@/graphql/queries/CurrentCompany.gql';
import CurrentUserQuery from '@/graphql/queries/CurrentUser.gql';

const localStorage = 'localStorage' in global && global.localStorage ? global.localStorage : null;
const sessionStorage = 'sessionStorage' in global && global.sessionStorage ? global.sessionStorage : null;

export default {
  computed: {
    ...mapRoot([ 'isNewDashboard' ]),
    redirectTo() {
      const { redirect } = this.$route.query;

      if (typeof redirect === 'string' && redirect.includes('://')) {
        if (process.env.NODE_ENV === 'production') {
          if (redirect.startsWith('exp://') || redirect.startsWith('rl-app')) {
            return redirect;
          }
        } else {
          if (redirect.startsWith('https://httpbin.org')) {
            return redirect;
          }
        }
      }

      return null;
    },
    nextRoute() {
      const { next } = this.$route.query;
      return next ? { path: next } : null;
    },
    identityProviders() {
      if (this.company && this.company.cognito) {
        const { poolDomain, clientID } = this.company.cognito;
        let { providers } = this.company.cognito;
        const { isSainsburys, origin } = this.$root;

        const opts = {
          client_id: clientID,
          response_type: 'token',
          redirect_uri: `${origin}/login/confirm`,
        };

        if (this.redirectTo) {
          opts.state = { ...opts.state, redirect: this.redirectTo };
        }

        if (this.nextRoute) {
          const { href } = this.$router.resolve(this.nextRoute);
          opts.state = { ...opts.state, next: href };
        }

        if (opts.state) {
          // Query string in query string so you can query whilst you string
          opts.state = qs.stringify(opts.state);
        }

        providers = (Array.isArray(providers) ? providers : []).map(({ type, handle, identifiers }) => ({
          type,
          ...(type === 'COGNITO' ? {
            url: `${poolDomain.replace('/authorize', '')}/login?${qs.stringify(opts)}`,
          } : {
            type,
            handle,
            identifiers,
            url: `${poolDomain.replace('/authorize', '')}/authorize?${qs.stringify({ ...opts, identity_provider: handle || undefined })}`,
          }),
        }));

        if (isSainsburys) {
          // @TODO A smarter sorting function would be appreciated here
          // providers = cleverSort(providers, [ 'sainsburys-', 'argos-' ]);
          providers = providers.reverse();
        }

        return providers;
      } else {
        return [];
      }
    },
    canAuthSingleSignOn() {
      return this.identityProviders.filter(({ type }) => type !== 'COGNITO').length > 0;
    },
    canAuthEmailPassword() {
      return this.identityProviders.filter(({ type }) => type === 'COGNITO').length > 0;
    },
    hasRegisterQuestionsToAsk() {
      const { authPreferences: { askUserForDepartment = false, askUserForLocation = false } = {}, customFields } = this.company;
      return askUserForDepartment || askUserForLocation || (Array.isArray(customFields) && customFields.length);
    },
  },
  methods: {
    /**
     * @link https://github.com/aws-amplify/amplify-js/blob/b997327d43417a5fbca663d2a58e299340e74bea/packages/amazon-cognito-identity-js/src/CognitoUserPool.js#L143-L170
     */
    getCognitoContextData(username) {
      if (typeof AmazonCognitoAdvancedSecurityData !== 'undefined') {
        /* eslint-disable */
        const amazonCognitoAdvancedSecurityDataConst = AmazonCognitoAdvancedSecurityData;
        /* eslint-enable */

        if (this.company && this.company.cognito) {
          const { poolID, clientID } = this.company.cognito;
          try {
            const advancedSecurityData = amazonCognitoAdvancedSecurityDataConst.getData(username, poolID, clientID);
            if (advancedSecurityData) {
              return advancedSecurityData;
            }
          } catch (err) {
            if (process.env.NODE_ENV !== 'production') {
              this.$log.error('getCognitoContextData', err);
            }
          }
        }
      }

      return null;
    },
    updateAuthResponse(client, data) {
      const { token, company, user } = { ...data };

      if (process.env.VUE_ENV === 'client' && typeof token === 'string' && token.length > 10) {
        ls.set('token', token);
      }
      if (company && company.id) {
        client.writeQuery({ query: CurrentCompanyQuery, data: { company } });
      }
      if (user && user.id) {
        client.writeQuery({ query: CurrentUserQuery, data: { user } });
      }
    },
    afterAuthenticated(nextRoute) {
      const { user } = this.$root;
      this.$log.debug('afterAuthenticated', nextRoute);
      const { href: next } = nextRoute ? this.$router.resolve(nextRoute) : {};

      if (user && user.role === 'EMPLOYEE') {
        const { departments, missingRequiredFields } = user;

        if (this.hasRegisterQuestionsToAsk && Array.isArray(missingRequiredFields) && missingRequiredFields.length) {
          const departmentIDs = departments.map(({ id }) => id);
          const matchingRequiredFields = missingRequiredFields.filter(({ key, departments: d }) => {
            if (!Array.isArray(d) || d.filter(({ id }) => departmentIDs.includes(id)).length) {
              return true;
            } else {
              return false;
            }
          });
          // If the employee has a missing required field, force them to complete it
          if (matchingRequiredFields.length) {
            const { query } = this.$route;
            return this.$router.push({
              name: 'RegisterQuestions',
              query: { ...query, next: next || (this.isNewDashboard ? '/dashboard-im' : '/dashboard') },
            });
          }
        }
      }

      // window.alert(`${this.redirectTo}`);

      if (typeof this.redirectTo === 'string') {
        this.redirectOnTo({
          token: ls.get('token'),
          email: user.emailAddress,
          next,
        });
      } else {
        this.$router.push(nextRoute || { name: 'Dashboard' });
      }
    },
    afterSignup(nextRoute) {
      const { user, isRecruiter } = this.$root;
      this.$log.debug('afterSignup', nextRoute);
      const { href: next, route } = nextRoute ? this.$router.resolve(nextRoute) : {};

      const { name, query } = this.$route;
      if (!isRecruiter && name !== 'RegisterQuestions' && this.hasRegisterQuestionsToAsk) {
        // @TODO The first page of onboarding should be these questions - they shouldn't be a separate flow!
        return this.$router.push({ name: 'RegisterQuestions', query });
      }

      if (typeof this.redirectTo === 'string') {
        this.redirectOnTo({
          token: ls.get('token'),
          email: user.emailAddress,
          next,
        });
      } else if (route && route.meta && route.meta.requireAuth !== true) {
        this.$router.push(next);
      } else {
        this.$router.push({ name: 'Onboarding', params: { nextRoute: nextRoute || route } });
      }
    },
    resumeSession() {
      return this.$apollo.query({ query: AuthResumeSessionQuery });
    },
    clearAuth() {
      this.clearToken();
      Object.values(this.$apollo.provider.clients).forEach(client => client.cache.reset());
      Sentry.configureScope(scope => scope.clear());
    },
    getToken() {
      return ls.get('token');
    },
    clearToken() {
      ls.remove('token');
      localStorage && localStorage.clear();
      sessionStorage && sessionStorage.clear();
    },
    redirectOnTo(query) {
      window.location = `${this.redirectTo}${this.redirectTo.includes('?') ? '&' : '?'}${qs.stringify(query)}`;
    },
  },
};

export function createIdentityProviderDetails(type, handle) {
  // return { icon, name, class? }
}



// WEBPACK FOOTER //
// ./src/mixins/auth.js